import { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import { confirmPopup } from 'primereact/confirmpopup';
import { DELETE_LOGO_CONFIRM_MSG, OR_BROWSE_FILES, REMOVE_LOGO } from '../../../constants/strings';
import { useTranslation } from '../../../hooks/useTranslation';

const ACCEPTED_TYPES = ['image/svg+xml', 'image/png'];

type LogoDropzoneProps = {
    id: string;
    label: string;
    dropText: string;
    hint: string;
    onFileChange?: (file: File | null) => void;
    onInvalidFile?: (file: File) => void;
    // logo already saved on the server, shown until a new file is picked
    savedUrl?: string | null;
    onRemoveSaved?: () => void;
    disabled?: boolean;
};

const languageTexts = {
    OR_BROWSE_FILES: OR_BROWSE_FILES,
    REMOVE_LOGO: REMOVE_LOGO,
    DELETE_LOGO_CONFIRM_MSG: DELETE_LOGO_CONFIRM_MSG,
};

function LogoDropzone({
    id,
    label,
    dropText,
    hint,
    onFileChange,
    onInvalidFile,
    savedUrl,
    onRemoveSaved,
    disabled = false,
}: Readonly<LogoDropzoneProps>) {
    const translatedText = useTranslation(languageTexts, 'none');
    const inputRef = useRef<HTMLInputElement>(null);

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const displayUrl = previewUrl ?? savedUrl ?? null;

    // release the object URL when the preview changes or the component unmounts
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handleFile = (file: File | undefined) => {
        if (!file || disabled) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            onInvalidFile?.(file);
            return;
        }
        setPreviewUrl(URL.createObjectURL(file));
        onFileChange?.(file);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleFile(event.target.files?.[0]);
        event.target.value = '';
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        handleFile(event.dataTransfer.files?.[0]);
    };

    const handleRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
        // an unsaved pick is simply discarded; a saved logo is deleted on the server after confirmation
        if (previewUrl) {
            setPreviewUrl(null);
            onFileChange?.(null);
            return;
        }
        confirmPopup({
            target: event.currentTarget,
            message: translatedText.DELETE_LOGO_CONFIRM_MSG,
            icon: 'ri-error-warning-line',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: () => onRemoveSaved?.(),
        });
    };

    return (
        <div className="logo-dropzone-wrapper">
            <label className="logo-dropzone-label" htmlFor={id}>
                {label}
            </label>
            <div
                className={`logo-dropzone ${isDragging ? 'dragging' : ''} ${displayUrl ? 'has-preview' : ''}`}
                data-testid={`${id}-dropzone`}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
            >
                {displayUrl ? (
                    <>
                        <button
                            type="button"
                            className="logo-dropzone-replace"
                            aria-label={translatedText.OR_BROWSE_FILES}
                            disabled={disabled}
                            onClick={() => inputRef.current?.click()}
                        >
                            <img className="logo-dropzone-preview" src={displayUrl} alt={label} />
                        </button>
                        <button
                            type="button"
                            className="logo-dropzone-remove"
                            aria-label={translatedText.REMOVE_LOGO}
                            disabled={disabled}
                            onClick={handleRemove}
                        >
                            <i className="ri-close-line"></i>
                        </button>
                    </>
                ) : (
                    <>
                        <i className="ri-image-line logo-dropzone-icon"></i>
                        <span className="logo-dropzone-text">{dropText}</span>
                        <button
                            type="button"
                            className="logo-dropzone-browse"
                            disabled={disabled}
                            onClick={() => inputRef.current?.click()}
                        >
                            {translatedText.OR_BROWSE_FILES}
                        </button>
                    </>
                )}
                <input
                    id={id}
                    ref={inputRef}
                    type="file"
                    className="d-none"
                    accept={ACCEPTED_TYPES.join(',')}
                    disabled={disabled}
                    onChange={handleInputChange}
                />
            </div>
            <span className="logo-dropzone-hint">{hint}</span>
        </div>
    );
}

export default LogoDropzone;
